import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Alert,
  LinearProgress
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useSnackbar } from 'notistack'
import requestService from '../../../services/requestService'
import mrdService from '../../../services/mrdservice'

const RequestForm = ({ open, onClose, request = null, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = Boolean(request)
  
  const [loading, setLoading] = useState(false)
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [mrdFiles, setMrdFiles] = useState([])
  const [formData, setFormData] = useState({
    requestType: '',
    requestedBy: '',
    requestorType: '',
    requestorId: '',
    requestorContact: '',
    requestorEmail: '',
    mrdFiles: [],
    requiredByDate: null,
    purpose: '',
    requestDetails: '',
    priority: 'MEDIUM',
    charges: 0
  })

  const requestTypes = [
    { value: 'LEGAL', label: 'Legal' },
    { value: 'INSURANCE', label: 'Insurance' },
    { value: 'RESEARCH', label: 'Research' },
    { value: 'PATIENT_COPY', label: 'Patient Copy' },
    { value: 'AUDIT', label: 'Audit' },
    { value: 'OTHER', label: 'Other' }
  ]

  const requestorTypes = [
    { value: 'PATIENT', label: 'Patient' },
    { value: 'RELATIVE', label: 'Relative' },
    { value: 'INSURANCE_COMPANY', label: 'Insurance Company' },
    { value: 'COURT', label: 'Court' },
    { value: 'POLICE', label: 'Police' },
    { value: 'DOCTOR', label: 'Doctor' },
    { value: 'RESEARCHER', label: 'Researcher' }
  ]

  const priorities = [
    { value: 'LOW', label: 'Low', color: 'default' },
    { value: 'MEDIUM', label: 'Medium', color: 'primary' },
    { value: 'HIGH', label: 'High', color: 'warning' }
  ]

  useEffect(() => {
    if (open) {
      loadMrdFiles()
      if (isEdit) {
        setFormData({
          requestType: request.requestType || '',
          requestedBy: request.requestedBy || '',
          requestorType: request.requestorType || '',
          requestorId: request.requestorId || '',
          requestorContact: request.requestorContact || '',
          requestorEmail: request.requestorEmail || '',
          mrdFiles: request.mrdFiles || [],
          requiredByDate: request.requiredByDate ? new Date(request.requiredByDate) : null,
          purpose: request.purpose || '',
          requestDetails: request.requestDetails || '',
          priority: request.priority || 'MEDIUM',
          charges: request.charges || 0
        })
      } else {
        // Reset form for new request
        setFormData({
          requestType: '',
          requestedBy: '',
          requestorType: '',
          requestorId: '',
          requestorContact: '',
          requestorEmail: '',
          mrdFiles: [],
          requiredByDate: null,
          purpose: '',
          requestDetails: '',
          priority: 'MEDIUM',
          charges: 0
        })
      }
    }
  }, [open, isEdit, request])

  const loadMrdFiles = async () => {
    setLoadingFiles(true)
    try {
      console.log('🔍 Loading MRD files...')
      
      // Check if user is authenticated
      const user = JSON.parse(localStorage.getItem('user'));
      const token = user?.token;
      if (!token) {
        console.log('❌ No authentication token found')
        enqueueSnackbar('Please login to access MRD files', { variant: 'warning' })
        return
      }

      const response = await mrdService.getFiles({ 
        page: 1, 
        limit: 100
        // Remove fileStatus filter to get all files
      })
      
      console.log('📁 MRD Files Response:', response.data)
      
      if (response.data.success) {
        const files = response.data.data || []
        setMrdFiles(files)
        console.log('✅ MRD Files loaded:', files.length)
        
        if (files.length === 0) {
          enqueueSnackbar('No MRD files found', { variant: 'info' })
        }
      } else {
        console.log('❌ MRD Files API returned success: false')
        enqueueSnackbar('Failed to load MRD files', { variant: 'error' })
      }
    } catch (error) {
      console.error('❌ Error loading MRD files:', error)
      console.error('Status:', error.response?.status)
      console.error('Message:', error.response?.data?.message)
      
      if (error.response?.status === 401) {
        enqueueSnackbar('Authentication required to access MRD files', { variant: 'error' })
      } else if (error.response?.status === 403) {
        enqueueSnackbar('You do not have permission to access MRD files', { variant: 'error' })
      } else {
        enqueueSnackbar('Failed to load MRD files', { variant: 'error' })
      }
    } finally {
      setLoadingFiles(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async () => {
    // Validation
    if (!formData.requestType || !formData.requestedBy || !formData.requestorType) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' })
      return
    }

    if (formData.mrdFiles.length === 0) {
      enqueueSnackbar('Please select at least one MRD file', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      const submitData = {
        ...formData,
        mrdFiles: formData.mrdFiles.map(file => file._id || file),
        requiredByDate: formData.requiredByDate?.toISOString()
      }

      if (isEdit) {
        await requestService.updateRequest(request._id, submitData)
        enqueueSnackbar('Request updated successfully', { variant: 'success' })
      } else {
        await requestService.createRequest(submitData)
        enqueueSnackbar('Request created successfully', { variant: 'success' })
      }

      onSuccess && onSuccess()
      onClose()
    } catch (error) {
      console.error('Error saving request:', error)
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to save request', 
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Request' : 'New File Request'}
      </DialogTitle>
      
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Request Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Request Type</InputLabel>
                <Select
                  value={formData.requestType}
                  label="Request Type"
                  onChange={(e) => handleInputChange('requestType', e.target.value)}
                >
                  {requestTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Priority */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                >
                  {priorities.map(priority => (
                    <MenuItem key={priority.value} value={priority.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={priority.label} 
                          color={priority.color} 
                          size="small" 
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Requested By */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Requested By"
                value={formData.requestedBy}
                onChange={(e) => handleInputChange('requestedBy', e.target.value)}
                placeholder="Name of person making request"
              />
            </Grid>

            {/* Requestor Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Requestor Type</InputLabel>
                <Select
                  value={formData.requestorType}
                  label="Requestor Type"
                  onChange={(e) => handleInputChange('requestorType', e.target.value)}
                >
                  {requestorTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Requestor ID */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Requestor ID"
                value={formData.requestorId}
                onChange={(e) => handleInputChange('requestorId', e.target.value)}
                placeholder="ID/License number"
              />
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Number"
                value={formData.requestorContact}
                onChange={(e) => handleInputChange('requestorContact', e.target.value)}
                placeholder="Phone number"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={formData.requestorEmail}
                onChange={(e) => handleInputChange('requestorEmail', e.target.value)}
                placeholder="Email address"
              />
            </Grid>

            {/* Required By Date */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Required By Date"
                value={formData.requiredByDate}
                onChange={(date) => handleInputChange('requiredByDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'When do you need the files?'
                  }
                }}
                minDate={new Date()}
              />
            </Grid>

            {/* Charges */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Charges (₹)"
                value={formData.charges}
                onChange={(e) => handleInputChange('charges', parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* MRD Files */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Available MRD Files: {loadingFiles ? 'Loading...' : mrdFiles.length}
                </Typography>
                <Button 
                  size="small" 
                  onClick={loadMrdFiles}
                  disabled={loadingFiles}
                  variant="outlined"
                >
                  {loadingFiles ? 'Loading...' : 'Refresh Files'}
                </Button>
              </Box>
              <Autocomplete
                multiple
                options={mrdFiles}
                loading={loadingFiles}
                getOptionLabel={(option) => `${option.ipdNumber || option.fileNumber || 'N/A'} - ${option.patientName || 'Unknown Patient'}`}
                value={formData.mrdFiles}
                onChange={(e, newValue) => handleInputChange('mrdFiles', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select MRD Files"
                    placeholder="Search and select files..."
                    required
                    helperText={
                      loadingFiles 
                        ? "Loading files..." 
                        : mrdFiles.length === 0 
                          ? "No files available - check console for errors" 
                          : `${mrdFiles.length} files available`
                    }
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={`${option.ipdNumber || option.fileNumber || 'N/A'} - ${option.patientName || 'Unknown'}`}
                      {...getTagProps({ index })}
                      key={option._id}
                    />
                  ))
                }
                noOptionsText={
                  loadingFiles 
                    ? "Loading files..." 
                    : mrdFiles.length === 0 
                      ? "No files found - check authentication" 
                      : "No matching files"
                }
              />
            </Grid>

            {/* Purpose */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                value={formData.purpose}
                onChange={(e) => handleInputChange('purpose', e.target.value)}
                placeholder="Brief purpose of the request"
                multiline
                rows={2}
              />
            </Grid>

            {/* Request Details */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Request Details"
                value={formData.requestDetails}
                onChange={(e) => handleInputChange('requestDetails', e.target.value)}
                placeholder="Detailed description of what is needed..."
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          {/* Validation Alert */}
          {formData.mrdFiles.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one MRD file for this request
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || formData.mrdFiles.length === 0}
        >
          {isEdit ? 'Update Request' : 'Create Request'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RequestForm